import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DynamicRibbonFormat, IHomeTileConfig } from "@/api_framework/api_modals/HomeTileConfig";
import { useHomeTileConfigAPI } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileConfigAPI";
import { useHomeTileListAPI } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { BackLink } from "./components";
import { ConfigListHeader, DynamicStatusModal } from "./ConfigList/ConfigListHeader";
import { DynamicRibbonStyleModal } from "./ConfigList/DynamicRibbonStyleModal";
import { ConfigFilters, ConfigTable, DeleteRuleModal } from "./ConfigList/ConfigTable";
import { DEFAULT_DYNAMIC_RIBBON_FORMAT, RibbonTypeFilter, StatusFilter, matchesSearch } from "./interface";
import { TileTable } from "./ConfigList/TileTable";
import { useConfigColumns } from "./ConfigList/useConfigColumns";
import { fromPersonaRule } from "./personaExpression";

const HomeTileConfigSetup = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { configList, isListLoading, listError, isDeleting, fetchTileConfigs, deleteConfig } = useHomeTileConfigAPI();
  const {
    tileList,
    isLoading: isTileListLoading,
    isUpdatingDynamicStatus,
    execute: fetchTiles,
    updateDynamicStatus,
  } = useHomeTileListAPI();

  const [params] = useSearchParams();
  const selectedTileId = params.get("tile_id") ?? "";
  const tileListUrl = getRouteKey("HOME_TILE_SETUP", "url");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [ribbonTypeFilter, setRibbonTypeFilter] = useState<RibbonTypeFilter>("ALL");
  const [deleteTarget, setDeleteTarget] = useState<IHomeTileConfig | null>(null);
  const [pendingDynamicStatus, setPendingDynamicStatus] = useState<boolean | null>(null);
  const [selectedRibbonFormat, setSelectedRibbonFormat] = useState<DynamicRibbonFormat | null>(null);
  const [isEditingDynamicRibbon, setIsEditingDynamicRibbon] = useState<boolean>(false);

  useEffect(() => {
    fetchTiles();
  }, [fetchTiles]);

  const loadConfigurations = useCallback(() => {
    if (selectedTileId) fetchTileConfigs(selectedTileId);
  }, [selectedTileId, fetchTileConfigs]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  const visibleTiles = useMemo(
    () =>
      ribbonTypeFilter === "ALL"
        ? tileList
        : tileList.filter((tile) => tile.is_dynamic_enabled === (ribbonTypeFilter === "DYNAMIC")),
    [tileList, ribbonTypeFilter],
  );

  const selectedTile = useMemo(
    () => tileList.find((tile) => tile.tile_id === selectedTileId),
    [tileList, selectedTileId],
  );
  const tileName = selectedTile?.tile_name ?? "this tile";

  const searchableConfigs = useMemo(
    () =>
      configList.map((config) => ({
        config,
        searchValues: [fromPersonaRule(config.persona_rule), config.ribbon_text, config.updated_by],
      })),
    [configList],
  );

  const visibleConfigs = useMemo(
    () =>
      searchableConfigs
        .filter(({ config, searchValues }) => {
          if (statusFilter !== "ALL" && config.status !== statusFilter) return false;
          return matchesSearch(searchTerm, searchValues);
        })
        .map(({ config }) => config),
    [searchableConfigs, searchTerm, statusFilter],
  );

  const activeCount = useMemo(() => configList.filter((config) => config.status === "ACTIVE").length, [configList]);

  const goToConfig = useCallback(
    (configId?: string) => {
      const query = new URLSearchParams({ tile_id: selectedTileId });
      if (configId) query.set("config_id", configId);
      navigate(`${getRouteKey("HOME_TILE_ONBOARD", "url")}?${query.toString()}`);
    },
    [getRouteKey, navigate, selectedTileId],
  );

  const addRule = useCallback(() => goToConfig(), [goToConfig]);

  const openTile = useCallback(
    (tileId: string) => navigate(`${tileListUrl}?tile_id=${encodeURIComponent(tileId)}`),
    [navigate, tileListUrl],
  );

  const columns = useConfigColumns({
    onEdit: goToConfig,
    onDelete: setDeleteTarget,
    statusFilter,
    onStatusFilterChange: setStatusFilter,
  });

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("ALL");
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const isDeleted = await deleteConfig(deleteTarget.id);
    if (isDeleted) {
      setDeleteTarget(null);
      fetchTiles();
    }
  };

  const requestDynamicStatus = (nextStatus: boolean) => {
    setSelectedRibbonFormat(DEFAULT_DYNAMIC_RIBBON_FORMAT);
    setPendingDynamicStatus(nextStatus);
  };

  const confirmDynamicStatus = async () => {
    if (!selectedTileId || pendingDynamicStatus === null) return;
    if (pendingDynamicStatus && !selectedRibbonFormat) return;
    const ribbonFormat = pendingDynamicStatus ? selectedRibbonFormat ?? undefined : undefined;
    const isUpdated = await updateDynamicStatus(selectedTileId, pendingDynamicStatus, undefined, ribbonFormat);
    if (isUpdated) {
      setPendingDynamicStatus(null);
      fetchTiles();
    }
  };

  const editDynamicRibbon = () => setIsEditingDynamicRibbon(true);

  return (
    <div className="px-5 py-4">
      {selectedTileId && <BackLink label="Back to tiles" onClick={() => navigate(tileListUrl)} />}

      <div className="mb-4">
        <h1 className="text-lg font-semibold leading-7 text-gray-900">Home Tile Configuration</h1>
      </div>

      <section className="w-full min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <ConfigListHeader
          selectedTileId={selectedTileId}
          selectedTile={selectedTile}
          isListLoading={isListLoading}
          configCount={configList.length}
          activeCount={activeCount}
          isUpdatingDynamicStatus={isUpdatingDynamicStatus}
          onRequestDynamicStatus={requestDynamicStatus}
          onEditDynamicRibbon={editDynamicRibbon}
          onAddRule={addRule}
        />

        {selectedTileId ? (
          <>
            <ConfigFilters searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            <ConfigTable
              columns={columns}
              configs={visibleConfigs}
              isLoading={isListLoading}
              error={listError}
              hasFilters={Boolean(searchTerm.trim()) || statusFilter !== "ALL"}
              tileName={selectedTile?.tile_name}
              onRetry={loadConfigurations}
              onClearFilters={clearFilters}
              onAddRule={addRule}
            />
          </>
        ) : (
          <TileTable
            tiles={visibleTiles}
            totalCount={tileList.length}
            isLoading={isTileListLoading}
            ribbonTypeFilter={ribbonTypeFilter}
            onRibbonTypeFilterChange={setRibbonTypeFilter}
            onSelect={openTile}
            onShowAll={() => setRibbonTypeFilter("ALL")}
            onRetry={fetchTiles}
          />
        )}
      </section>

      <DynamicStatusModal
        nextStatus={pendingDynamicStatus}
        tileName={tileName}
        isLoading={isUpdatingDynamicStatus}
        selectedFormat={selectedRibbonFormat}
        onSelectFormat={setSelectedRibbonFormat}
        onConfirm={confirmDynamicStatus}
        onClose={() => setPendingDynamicStatus(null)}
      />

      <DynamicRibbonStyleModal
        isOpen={isEditingDynamicRibbon}
        tileId={selectedTileId}
        tileName={tileName}
        isDynamicEnabled={Boolean(selectedTile?.is_dynamic_enabled)}
        currentRibbon={selectedTile?.dynamic_ribbon ?? null}
        currentFormat={selectedTile?.dynamic_ribbon_member_format ?? null}
        onClose={() => setIsEditingDynamicRibbon(false)}
        onSaved={fetchTiles}
      />

      <DeleteRuleModal
        target={deleteTarget}
        tileName={selectedTile ? `${selectedTile.tile_name} tile` : tileName}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default HomeTileConfigSetup;
